/**
 * DMP Section Editor
 *
 * TipTap WYSIWYG editor with toolbar, placeholder support,
 * and auto-save to Firestore.
 */

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Highlight } from '@tiptap/extension-highlight';
import { DMPPlaceholderExtension } from './DMPPlaceholder';
import {
  applyContentAction,
  editorContentAction,
  shouldConfirmSave,
  EditorTracker,
  isUserEditUpdate,
  shouldPromptBeforeUnload,
  type EditorEditState,
  type SectionSaveResult,
} from '../../features/documents/dmpEditorSync';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  RotateCcw,
  Save,
  Table as TableIcon,
  Undo,
  Redo,
} from 'lucide-react';

interface DMPEditorProps {
  /** The section whose text `content` is. Every save names the section it carries. */
  sectionId: string;
  content: string;
  autoGeneratedContent: string;
  /**
   * userChanged: whether the user has typed, pasted, filled a placeholder or
   * reset since this section was loaded. The panel saves nothing without it
   * (docgen reasons, round 5). sectionId: the section the text belongs to,
   * which is NOT always the selected one: a save flushed on a section switch
   * carries the section being left (round 5c).
   */
  onSave: (html: string, info: { userChanged: boolean; sectionId: string }) => void | Promise<SectionSaveResult | void>;
  readOnly?: boolean;
  /**
   * Where the editor reports the section it is editing, whether a save is
   * pending, and the HTML it last saved or loaded. The panel reads it before
   * taking on a snapshot's version (shouldObserveSectionVersion, round 5d).
   */
  editState?: MutableRefObject<EditorEditState | null>;
  /** True while this section's saves are refused as changed elsewhere (round 5e). */
  stale?: boolean;
  /** Lets the panel cancel a pending save before a discard (round 5f). */
  control?: MutableRefObject<DMPEditorControl | null>;
}

/** What the panel may ask the editor to do. */
export interface DMPEditorControl {
  /** Drop the pending autosave, saving nothing. */
  cancelPending: () => void;
  /** The editor's text right now, including typing not yet saved (round 5g). */
  currentHtml: () => string | null;
}

export default function DMPEditor({
  sectionId,
  content,
  autoGeneratedContent,
  onSave,
  readOnly = false,
  editState,
  stale = false,
  control,
}: DMPEditorProps) {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Track whether the user has actually interacted with the editor
  const userHasEdited = useRef(false);
  const isSettingContent = useRef(false);
  // The section whose text is in the editor now, and the latest onSave: the
  // debounced save and the flushes must use these, not values captured when
  // the editor was created (round 5c).
  const loadedSectionRef = useRef<string>(sectionId);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  // The section's latest incoming text: a confirmation applies only when it
  // is the confirmed text (round 5h).
  const contentRef = useRef(content);
  contentRef.current = content;
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);
  // What the editor holds (section, pending save, the HTML last saved or
  // loaded, whether the user changed it), as one state machine (round 5e).
  const trackerRef = useRef<EditorTracker | null>(null);
  if (!trackerRef.current) {
    trackerRef.current = new EditorTracker();
    trackerRef.current.load(sectionId, content);
  }
  const editStateRef = useRef(editState);
  editStateRef.current = editState;
  const staleRef = useRef(stale);
  staleRef.current = stale;

  /** Tell the panel what the editor holds now: sectionId, pendingSave, baselineHtml, userChanged. */
  const report = useCallback(() => {
    const target = editStateRef.current;
    if (target) target.current = trackerRef.current!.state();
  }, []);

  /** Save this text for the section in the editor now; it becomes the baseline. */
  const saveNow = useCallback((html: string, userChanged: boolean) => {
    const tracker = trackerRef.current!;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    tracker.saved(html);
    report();
    const sectionAtSave = loadedSectionRef.current;
    // Round 5g: only a write the panel confirms ('saved', after the version
    // check accepted it) resets "user changed"; the local echo is not proof,
    // because it carries the text even for a write about to be refused.
    // Round 5h: and only when the section's latest incoming text is the
    // confirmed text; a remote edit that arrived meanwhile was kept against,
    // and confirming would let its next re-emission be taken on.
    void Promise.resolve(onSaveRef.current(html, { userChanged, sectionId: loadedSectionRef.current })).then((result) => {
      if (
        shouldConfirmSave({
          result,
          sectionAtSave,
          loadedSectionId: loadedSectionRef.current,
          latestIncomingContent: contentRef.current,
          html,
        })
      ) {
        tracker.confirmed(html);
        if (saveTimerRef.current === null && !tracker.state().userChanged) userHasEdited.current = false;
        report();
      }
    });
  }, [report]);

  /** Save now whatever the user has typed for the section in the editor. */
  const flushPendingSave = useCallback(() => {
    if (!saveTimerRef.current) return;
    const ed = editorRef.current;
    if (ed && !ed.isDestroyed) saveNow(ed.getHTML(), userHasEdited.current);
    else {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      trackerRef.current!.cancelled();
      report();
    }
  }, [saveNow, report]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Highlight.configure({ multicolor: true }),
      DMPPlaceholderExtension,
    ],
    content,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[300px] px-4 py-3',
      },
    },
    onUpdate: ({ editor, transaction }) => {
      // Only the user typing counts: not a programmatic load, and not an
      // update with no document change (setEditable emits one; round 5g).
      if (!isUserEditUpdate({ settingContent: isSettingContent.current, docChanged: transaction.docChanged })) return;
      userHasEdited.current = true;
      // Debounced auto-save, for the section in the editor when it fires.
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        saveNow(editor.getHTML(), userHasEdited.current);
      }, 2000);
      trackerRef.current!.typed();
      report();
    },
  });
  editorRef.current = editor;

  // New content: a section switch, or a listener echo of this section.
  useEffect(() => {
    if (!editor) return;
    const action = editorContentAction({
      incomingSectionId: sectionId,
      loadedSectionId: loadedSectionRef.current,
      pendingSave: saveTimerRef.current !== null,
      incomingContent: content,
      editorHtml: editor.getHTML(),
      // Round 5f: user-changed text the store does not hold is kept too, so a
      // refused write that the SDK rolls back never replaces it.
      editState: trackerRef.current!.state(),
    });
    // 'keep': the user is typing in this section. Their text stays; the
    // pending save runs as usual. If the incoming text is a REMOTE edit (it
    // differs from what the editor last saved or loaded), the panel has not
    // taken on its version (shouldObserveSectionVersion), so that save is
    // refused and the user is told the section changed elsewhere.
    // The order of a switch lives in applyContentAction: flush the section
    // being left, then reset the flag, then record the new section, then load.
    // Round 5h: a kept text that differs from the baseline is remembered, so
    // a confirmation of an earlier save does not reset "user changed".
    // Round 5i: and reported at once, so a section switch asks first.
    if (action === 'keep') {
      trackerRef.current!.kept(content);
      report();
    }
    applyContentAction(action, {
      flush: flushPendingSave,
      resetUserChanged: () => {
        userHasEdited.current = false;
      },
      setLoadedSection: () => {
        loadedSectionRef.current = sectionId;
      },
      load: () => {
        isSettingContent.current = true;
        editor.commands.setContent(content, { emitUpdate: false });
        trackerRef.current!.load(sectionId, content);
        report();
        requestAnimationFrame(() => {
          isSettingContent.current = false;
        });
      },
    });
  }, [content, sectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The section became stale: keep the refused text for "Copy my text" (round 5f).
  useEffect(() => {
    if (!stale) return;
    trackerRef.current!.refused();
    report();
  }, [stale, report]);

  // The panel's discard cancels a pending save before anything else (round 5f).
  useEffect(() => {
    if (!control) return;
    control.current = {
      cancelPending: () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        trackerRef.current!.cancelled();
        report();
      },
      currentHtml: () => {
        const ed = editorRef.current;
        return ed && !ed.isDestroyed ? ed.getHTML() : null;
      },
    };
    return () => {
      control.current = null;
    };
  }, [control, report]);

  // Read-only follows the prop, so the panel's switch question is modal (round 5f).
  useEffect(() => {
    // emitUpdate false: a read-only toggle is not typing (round 5g).
    if (editor && !editor.isDestroyed) editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  // On unmount and on page leave, save what the user typed rather than drop
  // it; on page leave with an unsaved edit, also ask the browser to confirm.
  useEffect(() => {
    const onLeave = (event: BeforeUnloadEvent) => {
      // Ask only when something would be lost: a pending save, or a stale
      // section holding the user's changed text (round 5e).
      if (!shouldPromptBeforeUnload({
        pendingSave: saveTimerRef.current !== null,
        stale: staleRef.current,
        userChanged: userHasEdited.current,
      })) return;
      flushPendingSave();
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      flushPendingSave();
    };
  }, [flushPendingSave]);

  const handleReset = useCallback(() => {
    if (editor && autoGeneratedContent) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      // No update event, so no second (debounced) save that would mark the
      // section edited (round 5d); the one save below is the reset.
      editor.commands.setContent(autoGeneratedContent, { emitUpdate: false });
      // A reset is the user's own action; this is its one save. It does not
      // replace the refused text, which stays copyable (round 5g).
      trackerRef.current!.resetTo(autoGeneratedContent);
      report();
      onSave(autoGeneratedContent, { userChanged: true, sectionId: loadedSectionRef.current });
      setShowResetConfirm(false);
    }
  }, [editor, autoGeneratedContent, onSave, report]);

  const handleManualSave = useCallback(() => {
    if (editor) {
      // The Save button sends the text, and says whether anyone changed it.
      saveNow(editor.getHTML(), userHasEdited.current);
    }
  }, [editor, saveNow]);

  // Handle placeholder clicks
  useEffect(() => {
    if (!editor) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('dmp-placeholder')) {
        const description = target.getAttribute('data-description') || '';
        const value = prompt(`Enter value for: ${description}`);
        if (value !== null && value.trim()) {
          // Find the placeholder node position and replace it
          const { state } = editor;
          let pos: number | null = null;

          state.doc.descendants((node, nodePos) => {
            if (
              node.type.name === 'dmpPlaceholder' &&
              node.attrs.description === description &&
              pos === null
            ) {
              pos = nodePos;
            }
          });

          if (pos !== null) {
            editor
              .chain()
              .focus()
              .setTextSelection({ from: pos, to: pos + 1 })
              .deleteSelection()
              .insertContent(value.trim())
              .run();
          }
        }
      }
    };

    const editorEl = editor.view.dom;
    editorEl.addEventListener('click', handleClick);
    return () => editorEl.removeEventListener('click', handleClick);
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-wrap">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold"
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic"
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <Heading2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
            active={editor.isActive('heading', { level: 4 })}
            title="Heading 4"
          >
            <Heading3 className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
            title="Insert Table"
          >
            <TableIcon className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo className="h-4 w-4" />
          </ToolbarButton>

          <div className="flex-1" />

          {/* Reset button */}
          {showResetConfirm ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-red-600">Reset to generated?</span>
              <button
                onClick={handleReset}
                className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
              >
                Yes
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Reset to auto-generated content"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
          )}

          <button
            onClick={handleManualSave}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            title="Save now"
          >
            <Save className="h-3 w-3" />
            Save
          </button>
        </div>
      )}

      {/* Editor content */}
      <EditorContent editor={editor} />

      {/* Placeholder styles */}
      <style>{`
        .dmp-placeholder {
          display: inline;
          background-color: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 4px;
          padding: 1px 6px;
          font-size: 0.875rem;
          color: #92400e;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .dmp-placeholder:hover {
          background-color: #fde68a;
          border-color: #d97706;
        }
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 12px 0;
        }
        .ProseMirror th,
        .ProseMirror td {
          border: 1px solid #d1d5db;
          padding: 6px 10px;
          text-align: left;
        }
        .ProseMirror th {
          background-color: #f3f4f6;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

/** Toolbar button helper */
function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700'
          : disabled
          ? 'text-gray-300 cursor-not-allowed'
          : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}
